import { IValidationService, AIProvider, QNA_MAX_CONTEXT_MESSAGES } from './types';

export class QnAConfigValidationService implements IValidationService {
  private readonly GOOGLE_DOCS_ID_PATTERN = /^[a-zA-Z0-9-_]{25,}$/;

  validateMaxContextMessages(maxMessages: number): { isValid: boolean; error?: string } {
    if (!Number.isInteger(maxMessages)) {
      return { isValid: false, error: 'Max context messages must be an integer' };
    }

    if (maxMessages < 1 || maxMessages > QNA_MAX_CONTEXT_MESSAGES) {
      return {
        isValid: false,
        error: `Max context messages must be between 1 and ${QNA_MAX_CONTEXT_MESSAGES}`,
      };
    }

    return { isValid: true };
  }

  validateProvider(provider: string): { isValid: boolean; error?: string } {
    if (!provider || provider.trim() === '') {
      return { isValid: false, error: 'Provider cannot be empty' };
    }

    const validProviders = Object.values(AIProvider);
    if (!validProviders.includes(provider as AIProvider)) {
      return {
        isValid: false,
        error: `Provider must be one of: ${validProviders.join(', ')}`,
      };
    }

    return { isValid: true };
  }

  validateModel(model: string): { isValid: boolean; error?: string } {
    if (!model || model.trim() === '') {
      return { isValid: false, error: 'Model cannot be empty' };
    }

    // Model is free-form text to support both OpenAI and Google models
    if (model.length > 100) {
      return {
        isValid: false,
        error: 'Model name cannot exceed 100 characters',
      };
    }

    return { isValid: true };
  }

  validateMaxTokens(maxTokens: number): { isValid: boolean; error?: string } {
    if (!Number.isInteger(maxTokens)) {
      return { isValid: false, error: 'Max tokens must be an integer' };
    }

    if (maxTokens < 1 || maxTokens > 8192) {
      return {
        isValid: false,
        error: 'Max tokens must be between 1 and 8192',
      };
    }

    return { isValid: true };
  }

  validateTemperature(temperature: number): { isValid: boolean; error?: string } {
    if (typeof temperature !== 'number' || Number.isNaN(temperature)) {
      return { isValid: false, error: 'Temperature must be a number' };
    }

    if (temperature < 0 || temperature > 2) {
      return {
        isValid: false,
        error: 'Temperature must be between 0.0 and 2.0',
      };
    }

    return { isValid: true };
  }

  validateDocumentId(documentId: string): { isValid: boolean; error?: string } {
    if (!documentId || documentId.trim() === '') {
      return { isValid: true }; // Optional field
    }

    if (!this.GOOGLE_DOCS_ID_PATTERN.test(documentId)) {
      return {
        isValid: false,
        error: 'Invalid Google Docs document ID format. ID must contain only letters, numbers, hyphens and underscores',
      };
    }

    return { isValid: true };
  }
}
